/**
 * ToolForm — the tool create / edit fields. Demo page: /form-layouts (sections of a two-column form). The schema starts
 * from the generated `ToolUpsert` body; the name (a path parameter of the request) is the only UI-only field, and the
 * two JSON schemas are tightened to objects so text that is not an object never reaches the server. Field help is the
 * shared `TOOL_FIELD_HELP` map, read by the (?) popover and the Field Help panel.
 */
import React from 'react';
import { z } from 'zod';
import { zToolUpsert } from '../../generated/api/zod.gen.js';
import type { Tool, ToolUpsert } from '../../generated/api/types.gen.js';
import {
  FormFullWidth,
  FormSection,
  JsonFormField,
  SelectFormField,
  TextAreaFormField,
  TextFormField,
} from '../../components/form/index.js';
import { TOOL_FIELD_HELP } from '../../help/tool-fields.js';
import { HelpField } from './entity-ui/FieldHelpAside.js';
import { sentenceCase } from '../../components/ui/FilterSelect.js';

export const TOOL_CATEGORIES = [
  'market-data',
  'research',
  'analysis',
  'ml-inference',
  'communication',
  'execution',
  'memory',
  'code',
  'vision',
  'web',
  'database',
] as const;

const NAME_PATTERN = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;

export const toolFormSchema = zToolUpsert.extend({
  name: z.string().min(1).regex(NAME_PATTERN, 'Use lowercase letters, numbers and hyphens only'),
  version: z.string().min(1),
  description: z.string().min(1),
  inputSchema: z.record(z.string(), z.unknown()),
  outputSchema: z.record(z.string(), z.unknown()),
});

export type ToolFormValues = z.output<typeof toolFormSchema>;

export const EMPTY_TOOL_VALUES: ToolFormValues = {
  name: '',
  version: '1.0.0',
  category: 'research',
  description: '',
  inputSchema: {},
  outputSchema: {},
};

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/** Form values for an existing tool (the edit form's defaults). */
export function toolToFormValues(tool: Tool): ToolFormValues {
  return {
    name: tool.name,
    version: tool.version ?? '1.0.0',
    category: tool.category ?? 'research',
    description: tool.description ?? '',
    inputSchema: asObject(tool.inputSchema),
    outputSchema: asObject(tool.outputSchema),
  };
}

/** The request body for the form values (the name goes in the path). */
export function toolFormToUpsert(values: ToolFormValues): ToolUpsert {
  const { name: _name, ...body } = values;
  return body;
}

/**
 * The assistant reads and writes the form in its own vocabulary (the two schemas as JSON text). These two adapters
 * translate in both directions so its apply pills keep working.
 */
export function toolAssistantValues(values: ToolFormValues): Record<string, unknown> {
  return {
    ...values,
    inputSchema: JSON.stringify(values.inputSchema, null, 2),
    outputSchema: JSON.stringify(values.outputSchema, null, 2),
  };
}

function parseSchemaText(value: unknown): Record<string, unknown> | undefined {
  if (typeof value !== 'string') return value && typeof value === 'object' ? asObject(value) : undefined;
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? asObject(parsed) : undefined;
  } catch {
    return undefined;
  }
}

export function toolAssistantPatch(partial: Record<string, unknown>): Partial<ToolFormValues> {
  const patch: Partial<ToolFormValues> = {};
  for (const key of ['name', 'version', 'category', 'description'] as const) {
    const value = partial[key];
    if (typeof value === 'string') patch[key] = value;
  }
  const input = parseSchemaText(partial.inputSchema);
  if (input) patch.inputSchema = input;
  const output = parseSchemaText(partial.outputSchema);
  if (output) patch.outputSchema = output;
  return patch;
}

interface ToolFieldsProps {
  nameEditable: boolean;
  currentCategory?: string;
  /** Bumped when values are written from outside the fields (the assistant), so the JSON editors re-read them. */
  revision?: number;
}

export const ToolFields: React.FC<ToolFieldsProps> = ({ nameEditable, currentCategory, revision = 0 }) => {
  const categories: string[] = [...TOOL_CATEGORIES];
  // A tool can carry a category outside the standard list; keep it selectable so saving does not change it.
  if (currentCategory && !categories.includes(currentCategory)) categories.push(currentCategory);
  const categoryOptions = categories.map((value) => ({ value, label: sentenceCase(value) }));
  return (
    <>
      <FormSection title="Identity">
        <HelpField name="name">
          <TextFormField
            name="name"
            label="Name"
            placeholder="my-custom-tool"
            description={
              nameEditable
                ? 'Agents call the tool by this name. It cannot change after creation.'
                : 'Fixed after creation.'
            }
            help={TOOL_FIELD_HELP.name}
            disabled={!nameEditable}
            required
          />
        </HelpField>
        <HelpField name="version">
          <TextFormField name="version" label="Version" placeholder="1.0.0" help={TOOL_FIELD_HELP.version} required />
        </HelpField>
        <HelpField name="category">
          <SelectFormField
            name="category"
            label="Category"
            options={categoryOptions}
            help={TOOL_FIELD_HELP.category}
            required
          />
        </HelpField>
        <FormFullWidth>
          <HelpField name="description">
            <TextAreaFormField
              name="description"
              label="Description"
              rows={3}
              placeholder="What this tool does and when an agent should use it"
              help={TOOL_FIELD_HELP.description}
              required
            />
          </HelpField>
        </FormFullWidth>
      </FormSection>
      <FormSection title="Contract">
        <HelpField name="inputSchema">
          <JsonFormField
            key={`input-${revision}`}
            name="inputSchema"
            label="Input schema"
            rows={10}
            description="JSON Schema of the parameters."
            help={TOOL_FIELD_HELP.inputSchema}
            required
          />
        </HelpField>
        <HelpField name="outputSchema">
          <JsonFormField
            key={`output-${revision}`}
            name="outputSchema"
            label="Output schema"
            rows={10}
            description="JSON Schema of the result. Leave {} when unstructured."
            help={TOOL_FIELD_HELP.outputSchema}
            required
          />
        </HelpField>
      </FormSection>
    </>
  );
};
